/**
 * businessInsights.shared.ts — Helpers deterministas del explorador de gráficas.
 *
 * QUÉ HACE: concentra la construcción de filas demo (valores, velas OHLC,
 *   paleta de series y presets de vistas) para que los archivos de datos de cada
 *   grupo declaren tarjetas compactas y consistentes. Todo es DETERMINISTA (hash
 *   FNV-1a, sin `Math.random`): la misma tarjeta produce siempre los mismos
 *   números y no hay parpadeos entre renders.
 *
 * QUÉ INVOCA: tipos de `businessInsights.types.ts`.
 * QUÉ EXPORTA: INSIGHT_COLORS, INSIGHT_VIEW_PRESETS, insightHash, insightRow,
 *   insightCandleRow, buildInsightCandle.
 *
 * QUÉ NO DEBE HACER: no lee storage, no hace fetch, no conoce React ni UI.
 */
import type {
  BusinessInsightCandle,
  BusinessInsightDataset,
  BusinessInsightRow,
  BusinessInsightViewId,
} from "../types/businessInsights.types";
import { CHART_VIEW_MENU_ORDER } from "@/components/ui";

/**
 * Vistas del orden estándar de Métricas (las 9 base) tipadas para el explorador.
 * `CHART_VIEW_MENU_ORDER` es la fuente de verdad compartida con el resto de
 * gráficas del Resumen.
 */
const INSIGHT_BASE_VIEWS: readonly BusinessInsightViewId[] =
  CHART_VIEW_MENU_ORDER as readonly BusinessInsightViewId[];

/**
 * Vistas efectivas de una tarjeta del explorador: **siempre** las 9 vistas base
 * (Barras verticales, Barras horizontales, Línea, Velas japonesas, Matriz,
 * Radar, Dona, Treemap y Tabla), en ese orden, más las vistas extra que declare
 * la tarjeta (apiladas, burbujas, progreso, semáforo, banda, combinada, gauge)
 * como adicionales. Así todas las tarjetas comparten el mismo selector.
 */
export function resolveInsightViews(
  dataset: Pick<BusinessInsightDataset, "views">,
): BusinessInsightViewId[] {
  const extras = dataset.views.filter(
    (view) => !INSIGHT_BASE_VIEWS.includes(view),
  );
  return [...INSIGHT_BASE_VIEWS, ...extras];
}

/** Paleta semántica de series del explorador (mismos tonos que Métricas). */
export const INSIGHT_COLORS = {
  caja: "#2563eb",
  web: "#a855f7",
  gastos: "#ef4444",
  exito: "#10b981",
  alerta: "#f59e0b",
  neutro: "#94a3b8",
  info: "#06b6d4",
  violeta: "#8b5cf6",
  naranja: "#f97316",
} as const;

/**
 * Presets de vistas por tipo de tarjeta. Declaran la **vista inicial** de la
 * tarjeta (la primera del preset) y las **vistas adicionales** propias de su
 * tipo de dato; el menú final siempre añade las 9 vistas base por delante
 * (`resolveInsightViews`), así que todas las tarjetas comparten el mismo
 * selector. Las 9 base: bar, barHorizontal, line, velas, matriz, radar, donut,
 * treemap y tabla.
 *  - `temporal` / `temporalLineFirst`: vista inicial barras / línea (con velas).
 *  - `temporalSinVelas` / `temporalSinVelasLineFirst`: sin OHLC.
 *  - `ranking` / `rankingHorizontal`: listas ordenadas (barras / horizontales).
 *  - `distribucion` / `distribucionHorizontal`: proporciones.
 *  - `matriz`: la matriz primero (heatmap hora × día / cohortes).
 *  - `tablaFirst`: la tabla primero (listados de gestión).
 */
export const INSIGHT_VIEW_PRESETS = {
  temporal: [
    "bar",
    "line",
    "matriz",
    "donut",
    "treemap",
    "barHorizontal",
    "tabla",
    "velas",
  ],
  temporalLineFirst: [
    "line",
    "bar",
    "matriz",
    "donut",
    "treemap",
    "barHorizontal",
    "tabla",
    "velas",
  ],
  temporalSinVelas: [
    "bar",
    "line",
    "matriz",
    "donut",
    "treemap",
    "barHorizontal",
    "tabla",
  ],
  temporalSinVelasLineFirst: [
    "line",
    "bar",
    "matriz",
    "donut",
    "treemap",
    "barHorizontal",
    "tabla",
  ],
  ranking: [
    "bar",
    "barHorizontal",
    "line",
    "donut",
    "treemap",
    "radar",
    "tabla",
    "matriz",
  ],
  rankingHorizontal: [
    "barHorizontal",
    "bar",
    "line",
    "donut",
    "treemap",
    "radar",
    "tabla",
    "matriz",
  ],
  distribucion: [
    "bar",
    "barHorizontal",
    "line",
    "donut",
    "treemap",
    "radar",
    "tabla",
  ],
  distribucionHorizontal: [
    "barHorizontal",
    "bar",
    "line",
    "donut",
    "treemap",
    "radar",
    "tabla",
  ],
  matriz: [
    "matriz",
    "bar",
    "line",
    "barHorizontal",
    "donut",
    "treemap",
    "tabla",
  ],
  cohorte: ["matriz", "tabla", "bar", "line", "barHorizontal"],
  tablaFirst: [
    "tabla",
    "bar",
    "barHorizontal",
    "line",
    "donut",
    "treemap",
    "matriz",
  ],
} as const satisfies Record<string, readonly BusinessInsightViewId[]>;

/** Hash determinista (FNV-1a) para variar los valores demo sin aleatoriedad. */
export function insightHash(seed: string): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Fila simple: etiqueta corta, etiqueta completa opcional y valores por serie. */
export function insightRow(
  name: string,
  values: Record<string, number>,
  fullName?: string,
): BusinessInsightRow {
  return { name, fullName, values };
}

/**
 * Vela OHLC determinista alrededor de un valor. Se usa solo en datos demo de la
 * sección de pruebas: la apertura/cierre oscilan ±14 % y el máximo/mínimo abren
 * el rango un 4 % para que la vela tenga cuerpo y mechas legibles.
 */
export function buildInsightCandle(
  value: number,
  seed: string,
): BusinessInsightCandle {
  const h = insightHash(seed);
  const open = Math.round(value * (0.86 + (h % 11) / 100));
  const close = Math.round(value * (0.9 + ((h >>> 4) % 13) / 100));
  const pad = Math.max(1, Math.abs(value) * 0.04);
  const high = Math.round(Math.max(value, open, close) + pad);
  const low = Math.round(Math.min(value, open, close) - pad);
  return { open, high, low, close };
}

/** Fila con vela OHLC derivada de su valor principal (vista de velas). */
export function insightCandleRow(
  name: string,
  values: Record<string, number>,
  seed: string,
  fullName?: string,
): BusinessInsightRow {
  const base = values[Object.keys(values)[0]] ?? 0;
  return {
    name,
    fullName,
    values,
    candle: buildInsightCandle(base, `${seed}:${name}`),
  };
}

/**
 * Genera valores enteros deterministas a partir de una base y un rango, uno por
 * etiqueta. Evita escribir a mano series largas (24 horas × 7 días) manteniendo
 * la reproducibilidad.
 */
export function insightSeededValues(
  seed: string,
  labels: readonly string[],
  base: number,
  spread: number,
): number[] {
  return labels.map((label) => {
    const h = insightHash(`${seed}:${label}`);
    const factor = 1 + ((h % (spread * 2 + 1)) - spread) / 100;
    return Math.max(1, Math.round(base * factor));
  });
}
